import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useSuperAdminAuthStore } from '@/store/superAdminAuthStore';
import { ROLES } from '@/constants';
import { AppLoader } from '@/components/common/AppLoader';
import { homePathAfterLogin, isMarketingUser } from '@/utils/helpers';

export function ProtectedRoute() {
  const location = useLocation();
  const hydrated = useAuthStore((s) => s.hydrated);
  const authState = useAuthStore((s) => s.authState);
  const isBiometricLocked = useAuthStore((s) => s.isBiometricLocked);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const impersonation = useSuperAdminAuthStore((s) => s.impersonation);
  const isPlatformAuth = useSuperAdminAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);

  if (!hydrated || authState === 'AUTH_BOOTSTRAPPING') {
    return <AppLoader label="Verifying session..." />;
  }

  if (isBiometricLocked || authState === 'BIOMETRIC_REQUIRED') {
    return <Navigate to="/login" replace state={{ from: location, biometricRequired: true }} />;
  }

  if (!isAuthenticated() && !isPlatformAuth) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Marketing users belong to Marketing portal
  if (isMarketingUser(user)) {
    return <Navigate to="/marketing" replace />;
  }

  // Watchman / Security Guard users belong to dedicated Watchman portal
  const isWatchmanOnly = Boolean(
    user?.roles?.includes('WATCHMAN') &&
    !isSuperAdmin() &&
    !user?.roles?.some((r) =>
      [
        ROLES.PRESIDENT,
        ROLES.VICE_PRESIDENT,
        ROLES.GENERAL_SECRETARY,
        ROLES.JOINT_SECRETARY,
        ROLES.TREASURER,
        ROLES.COMMITTEE_MEMBER,
        ROLES.EXECUTIVE_MEMBER,
      ].includes(r as any)
    )
  );

  if (isWatchmanOnly) {
    const isWatchmanAllowedRoute =
      location.pathname.startsWith('/watchman') ||
      location.pathname.startsWith('/gate-pass') ||
      location.pathname === '/profile' ||
      location.pathname === '/change-password' ||
      location.pathname === '/403';
    if (!isWatchmanAllowedRoute) {
      return <Navigate to="/watchman" replace />;
    }
  }

  // Super Admin belongs to Platform scope, only allowed in Society shell via Support Mode
  const isSuperAdminUser = isSuperAdmin() || Boolean(isPlatformAuth);
  if (isSuperAdminUser && !impersonation?.isImpersonating) {
    if (location.pathname === '/profile') {
      return <Navigate to="/super-admin/profile" replace />;
    }
    if (location.pathname === '/change-password') {
      return <Navigate to="/super-admin/change-password" replace />;
    }
    if (location.pathname === '/notifications' || location.pathname.startsWith('/notifications/')) {
      return <Navigate to="/super-admin/notifications" replace />;
    }
    const isSharedPlatformRoute =
      location.pathname.startsWith('/documentation') ||
      location.pathname.startsWith('/testing');
    if (!isSharedPlatformRoute) {
      return <Navigate to="/super-admin" replace />;
    }
  }

  // Society license expired / locked check: restrict all society routes except license renewal, profile, password
  if (user?.license?.locked && !isSuperAdminUser) {
    const isAllowedLockedRoute =
      location.pathname.startsWith('/settings/license') ||
      location.pathname === '/profile' ||
      location.pathname === '/change-password' ||
      location.pathname === '/403';
    if (!isAllowedLockedRoute) {
      return <Navigate to="/settings/license" replace />;
    }
  }

  return <Outlet />;
}

export function GuestRoute() {
  const hydrated = useAuthStore((s) => s.hydrated);
  const authState = useAuthStore((s) => s.authState);
  const isBiometricLocked = useAuthStore((s) => s.isBiometricLocked);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);

  if (!hydrated || authState === 'AUTH_BOOTSTRAPPING') {
    return <AppLoader label="Loading..." />;
  }

  // If biometric authentication is required, render login page for biometric prompt
  if (isBiometricLocked || authState === 'BIOMETRIC_REQUIRED') {
    return <Outlet />;
  }

  if (isAuthenticated()) {
    return <Navigate to={homePathAfterLogin(user)} replace />;
  }

  return <Outlet />;
}

interface RoleRouteProps {
  roles: string[];
  children?: never;
}

/**
 * @deprecated Prefer `PermissionRoute` (permission/anyOf) for feature pages.
 * Retained only for any remaining role-only gates.
 */
export function RoleRoute({ roles }: RoleRouteProps) {
  const hasRole = useAuthStore((s) => s.hasRole);
  const isPlatformAuth = useSuperAdminAuthStore((s) => s.isAuthenticated);
  const isSuperAdminPlatform = Boolean(isPlatformAuth) && roles.includes(ROLES.SUPER_ADMIN);
  const allowed = hasRole(roles) || isSuperAdminPlatform;

  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

/** Five unique office-bearers only — Super Admin is not enough unless they hold the post. */
export function OfficeBearerRoute() {
  const hasRole = useAuthStore((s) => s.hasRole);
  const allowed = hasRole([
    ROLES.PRESIDENT,
    ROLES.VICE_PRESIDENT,
    ROLES.GENERAL_SECRETARY,
    ROLES.JOINT_SECRETARY,
    ROLES.TREASURER,
  ]);

  if (!allowed) {
    return <Navigate to="/403" replace />;
  }

  return <Outlet />;
}

interface PermissionRouteProps {
  /** Hierarchical feature key checked against Society Access Control (e.g. 'finance.expenses'). */
  featureKey?: string;
  /** All listed permissions must be granted (AND). */
  permission?: string | string[];
  /** At least one listed permission must be granted (OR). */
  anyOf?: string[];
  /**
   * Super Admin is the VYORA product owner (societies + licenses), not a society operator.
   * Society routes send them to the dashboard; platform routes are Super Admin only.
   */
  audience?: 'society' | 'platform' | 'both';
  children?: never;
}

/** Guards a route subtree by feature entitlement and permission, redirecting to /403 when denied. */
export function PermissionRoute({
  featureKey,
  permission,
  anyOf,
  audience = 'society',
}: PermissionRouteProps) {
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const can = useAuthStore((s) => s.can);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const hasFeature = useAuthStore((s) => s.hasFeature);

  if (isSuperAdmin()) {
    if (audience === 'society') {
      return <Navigate to="/dashboard" replace />;
    }
    return <Outlet />;
  }

  if (audience === 'platform') {
    return <Navigate to="/403" replace />;
  }

  // Check society-level feature toggle
  if (featureKey && !hasFeature(featureKey)) {
    return <Navigate to="/403" replace />;
  }

  const allowed = anyOf?.length
    ? hasAnyPermission(anyOf)
    : permission
      ? can(permission)
      : true;

  if (!allowed) {
    return <Navigate to="/403" replace />;
  }

  return <Outlet />;
}

export function FeatureRoute({ featureKey }: { featureKey: string }) {
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const hasFeature = useAuthStore((s) => s.hasFeature);

  if (isSuperAdmin()) {
    return <Outlet />;
  }

  if (!hasFeature(featureKey)) {
    return <Navigate to="/403" replace />;
  }

  return <Outlet />;
}

export function MarketingRoute() {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  const isMarketing = Boolean(
    isMarketingUser(user) ||
    isSuperAdmin()
  );

  if (!isMarketing) {
    return <Navigate to="/403" replace />;
  }

  return <Outlet />;
}
